'use client';
import { forwardRef, useState, useRef, useId, isValidElement } from 'react';
import clsx from 'clsx';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { UseFormResetField } from 'react-hook-form';

import { mergeRefs } from '../../../utils';

import { Alert } from '../../alert/Alert';

export type SelectProps = {
  ref?: React.Ref<HTMLSelectElement>;
  name: string;
  label?: string;
  icon?: React.ReactNode;
  helperText?: string;
  placeholder?: string;
  error?: string;
  resetField?:
    | UseFormResetField<Record<string, string>>
    | ((
        name: string,
        options?:
          | Partial<{
              keepDirty: boolean;
              keepTouched: boolean;
              keepError: boolean;
              defaultValue: any;
            }>
          | undefined
      ) => void)
    | undefined;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef(function Select(
  {
    name,
    label,
    icon,
    helperText,
    error,
    resetField,
    className,
    children,
    ...props
  }: SelectProps,
  ref: React.ForwardedRef<HTMLSelectElement>
) {
  const id = useId();
  const inputRef = useRef<HTMLSelectElement | null>(null);
  const [hasValue, setHasValue] = useState(
    !!(props.value || props.defaultValue)
  );

  function resetHandler(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (typeof resetField === 'function') {
      resetField(name, {
        keepDirty: true,
        keepTouched: true,
      });
    } else if (inputRef.current instanceof HTMLSelectElement) {
      inputRef.current.value === '';
      setHasValue(false);
    }
  }

  function changeHandler(event: React.ChangeEvent<HTMLSelectElement>) {
    if (!hasValue && event.currentTarget.value) {
      setHasValue(true);
    }

    if (hasValue && !event.currentTarget.value) {
      setHasValue(false);
    }

    if (typeof props.onChange === 'function') {
      props.onChange(event);
    }
  }

  const centeredContent = clsx('flex justify-center items-center');

  return (
    <div className={clsx('text-neutral-80', className)}>
      {label && (
        <label htmlFor={props.id || id} className={clsx('cap-base')}>
          {label}
        </label>
      )}
      <div className={clsx('relative', 'my-2')}>
        <select
          ref={mergeRefs(ref, inputRef)}
          id={id}
          name={name}
          aria-invalid={!!error}
          className={clsx(
            'appearance-none',
            !icon ? 'pl-4' : 'pl-12',
            'pr-12',
            'h-14',
            'w-full',
            'rounded-lg',
            'bg-neutral-0',
            'border-1.5',
            'border-neutral-60',
            'elevation-neutral-sm',
            'outline-none',
            'focus:border-neutral-80',
            'focus:ring-2 focus:ring-offset-2 focus:ring-focus',
            {
              ['hover:border-neutral-70 hover:ring-4 hover:ring-neutral-50 hover:checked:bg-neutral-70']:
                !error,
              ['border-neutral-30 ring-2 ring-offset-2 ring-warning-50']:
                !!error,
            }
          )}
          {...props}
          onChange={changeHandler}
        >
          {props.placeholder && (
            <option hidden value="">
              {props.placeholder}
            </option>
          )}
          {children}
        </select>

        {isValidElement(icon) && (
          <span
            className={clsx(
              'absolute',
              'top-0',
              'left-0',
              'w-12',
              'h-full',
              centeredContent
            )}
          >
            {icon}
          </span>
        )}
        {hasValue && typeof resetHandler === 'function' && (
          <button
            onClick={(event) => {
              setHasValue(false);
              resetHandler(event);
            }}
            className={clsx(
              'absolute',
              'top-0',
              'right-10',
              'w-12',
              'h-full',
              centeredContent
            )}
          >
            <XMarkIcon className={clsx('w-5', 'h-5')} />
          </button>
        )}
        <span
          className={clsx(
            'absolute',
            'top-0',
            'right-3',
            'w-9',
            'h-full',
            'pointer-events-none',
            centeredContent
          )}
        >
          <ChevronDownIcon className={clsx('w-5', 'h-5')} />
        </span>
      </div>
      {helperText && (
        <p className={clsx('cap-xs', 'text-neutral-70')}>{helperText}</p>
      )}
      {error && <Alert type="warning" message={error} inline={true} />}
    </div>
  );
});

export default Select;
